import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { STATUS_STYLES } from '../constants';

const { FiTruck, FiMapPin, FiClock, FiCheck, FiPrinter, FiNavigation, FiPackage, FiX } = FiIcons;

// Initial dispatch data
const INITIAL_DISPATCH_DATA = {
  pending: [
    {
      id: 1,
      orderId: 'ORD-2024-0156',
      customer: 'Fresh Market Retailers',
      address: '123 Market Street, Cape Town',
      items: 8,
      totalValue: 45750,
      priority: 'high',
      timeSlot: '09:00 - 11:00',
      notes: 'Fragile items - handle with care'
    },
    {
      id: 2,
      orderId: 'ORD-2024-0157',
      customer: 'City Supermarket Chain',
      address: '456 Commerce Ave, Johannesburg',
      items: 12,
      totalValue: 78900,
      priority: 'medium',
      timeSlot: '11:00 - 13:00',
      notes: 'Delivery dock access required'
    }
  ],
  inTransit: [
    {
      id: 3,
      orderId: 'ORD-2024-0154',
      customer: 'Organic Food Co',
      address: '789 Green Lane, Durban',
      driver: 'John Smith',
      vehicle: 'VH-001',
      estimatedArrival: '10:45 AM',
      progress: 75,
      items: 6,
      totalValue: 23400
    },
    {
      id: 4,
      orderId: 'ORD-2024-0155',
      customer: 'Local Grocers Ltd',
      address: '321 Food Plaza, Pretoria',
      driver: 'Sarah Johnson',
      vehicle: 'VH-002',
      estimatedArrival: '11:30 AM',
      progress: 45,
      items: 9,
      totalValue: 56700
    }
  ],
  delivered: [
    {
      id: 5,
      orderId: 'ORD-2024-0152',
      customer: 'Premium Fresh Markets',
      address: '654 Elite Street, Cape Town',
      driver: 'Mike Wilson',
      deliveredAt: '09:15 AM',
      proofUrl: '/proof/ORD-2024-0152.jpg',
      items: 15,
      totalValue: 89400,
      rating: 5
    }
  ]
};

// Load dispatch data from localStorage
const loadDispatchData = () => {
  try {
    const saved = localStorage.getItem('fresh-board-dispatch-data');
    return saved ? JSON.parse(saved) : INITIAL_DISPATCH_DATA;
  } catch {
    return INITIAL_DISPATCH_DATA;
  }
};

// Load invoices from localStorage
const loadInvoices = () => {
  try {
    const saved = localStorage.getItem('fresh-board-invoices');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

const Dispatch = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [dispatchNotePreview, setDispatchNotePreview] = useState(null);
  const [dispatchData, setDispatchData] = useState(loadDispatchData);

  // Save dispatch data to localStorage
  React.useEffect(() => {
    localStorage.setItem('fresh-board-dispatch-data', JSON.stringify(dispatchData));
  }, [dispatchData]);

  // Confirm delivery and move order to delivered
  const confirmDelivery = (order) => {
    const now = new Date();
    const deliveredAt = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    // Create delivered order
    const deliveredOrder = {
      ...order,
      deliveredAt,
      proofUrl: `/proof/${order.orderId}.jpg`,
      rating: 0  // No rating yet
    };

    // Create invoice for this delivery
    const invoice = {
      id: `INV-${now.getFullYear()}-${String(Date.now()).slice(-4)}`,
      orderId: order.orderId,
      customer: order.customer,
      amount: order.totalValue,
      date: now.toISOString().split('T')[0],
      status: 'pending',
      items: order.items
    };

    // Save invoice to localStorage
    const existingInvoices = loadInvoices();
    localStorage.setItem('fresh-board-invoices', JSON.stringify([invoice, ...existingInvoices]));

    // Update dispatch data
    setDispatchData(prev => ({
      ...prev,
      inTransit: prev.inTransit.filter(o => o.id !== order.id),
      delivered: [deliveredOrder, ...prev.delivered]
    }));
  };

  const getPriorityColor = (priority) => {
    const darkPriorityStyles = {
      high: 'bg-red-500/10 text-red-400 border-red-500/30',
      medium: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
      low: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
    };
    return darkPriorityStyles[priority] || 'bg-[#1c2822] text-[#a5c4ad] border-[#243129]';
  };

  const dispatchMetrics = {
    onTimeDelivery: 94.2,
    totalDeliveries: 156,
    averageRouteTime: 42,
    activeDrivers: 8
  };

  const tabs = [
    { id: 'pending', label: 'Warehouse Queue', count: dispatchData.pending.length },
    { id: 'inTransit', label: 'In Transit', count: dispatchData.inTransit.length },
    { id: 'delivered', label: 'Delivered Today', count: dispatchData.delivered.length }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9]">
            Dispatch <span className="text-[#84cc16]">&</span> Fulfillment
          </h1>
          <p className="text-[#6b8772] font-mono text-sm mt-1">Order fulfillment and delivery tracking</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="bg-lime-500/10 border border-lime-500/20 rounded-lg px-4 py-2">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-lime-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-lime-400">{dispatchMetrics.activeDrivers} Drivers Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3b32] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">On-time Delivery</p>
              <p className="text-2xl font-bold text-lime-400 mt-1">{dispatchMetrics.onTimeDelivery}%</p>
            </div>
            <div className="bg-lime-500/10 border border-lime-500/20 rounded-lg p-3">
              <SafeIcon icon={FiCheck} className="w-6 h-6 text-lime-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3b32] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Total Deliveries</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{dispatchMetrics.totalDeliveries}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <SafeIcon icon={FiTruck} className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3b32] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Avg Route Time</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">{dispatchMetrics.averageRouteTime}m</p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3b32] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Active Drivers</p>
              <p className="text-2xl font-bold text-amber-400 mt-1">{dispatchMetrics.activeDrivers}</p>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <SafeIcon icon={FiNavigation} className="w-6 h-6 text-amber-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="bg-[#111916] rounded-xl border border-[#243129]">
        <div className="border-b border-[#243129]">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#84cc16] text-[#84cc16]'
                    : 'border-transparent text-[#6b8772] hover:text-[#a5c4ad]'
                }`}
              >
                <span className="font-medium">{tab.label}</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  activeTab === tab.id
                    ? 'bg-lime-500/10 text-lime-400 border border-lime-500/30'
                    : 'bg-[#1c2822] text-[#6b8772] border border-[#243129]'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'pending' && (
            <div className="space-y-4">
              {dispatchData.pending.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(order.priority)}`}>
                        {order.priority.toUpperCase()}
                      </span>
                      <div>
                        <h4 className="font-medium text-[#e8f5e9]">{order.orderId}</h4>
                        <p className="text-sm text-[#6b8772]">{order.customer}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#e8f5e9]">N$ {order.totalValue.toLocaleString()}</p>
                      <p className="text-sm text-[#6b8772]">{order.items} items</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center space-x-2 text-sm text-[#a5c4ad]">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 text-[#6b8772]" />
                      <span>{order.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-[#a5c4ad]">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-[#6b8772]" />
                      <span>Time Slot: {order.timeSlot}</span>
                    </div>
                  </div>

                  {order.notes && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-3">
                      <p className="text-sm text-amber-400">Note: {order.notes}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPackage} className="w-4 h-4 text-[#6b8772]" />
                      <span className="text-sm text-[#6b8772]">Ready for dispatch</span>
                    </div>
                    <button
                      onClick={() => setDispatchNotePreview(order)}
                      className="bg-gradient-to-r from-lime-600 to-lime-500 text-white px-4 py-2 rounded-lg hover:from-lime-500 hover:to-lime-400 transition-all flex items-center space-x-2 shadow-lg shadow-lime-500/20"
                    >
                      <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                      <span>Print Dispatch Note</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {activeTab === 'inTransit' && (
            <div className="space-y-4">
              {dispatchData.inTransit.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-[#e8f5e9]">{order.orderId}</h4>
                      <p className="text-sm text-[#6b8772]">{order.customer}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#e8f5e9]">N$ {order.totalValue.toLocaleString()}</p>
                      <p className="text-sm text-lime-400">ETA: {order.estimatedArrival}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center space-x-2 text-sm text-[#a5c4ad]">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 text-[#6b8772]" />
                      <span>{order.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-[#a5c4ad]">
                      <SafeIcon icon={FiTruck} className="w-4 h-4 text-[#6b8772]" />
                      <span>{order.driver} - {order.vehicle}</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm text-[#a5c4ad] mb-1">
                      <span>Delivery Progress</span>
                      <span className="text-lime-400">{order.progress}%</span>
                    </div>
                    <div className="w-full bg-[#1c2822] rounded-full h-2">
                      <div
                        className="bg-lime-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${order.progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-lime-400 rounded-full animate-pulse"></div>
                      <span className="text-sm text-lime-400 font-medium">Live Tracking Active</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button className="text-[#84cc16] hover:text-lime-300 text-sm font-medium transition-colors">
                        View Live Map
                      </button>
                      <button
                        onClick={() => confirmDelivery(order)}
                        className="bg-gradient-to-r from-lime-600 to-lime-500 text-white px-4 py-2 rounded-lg hover:from-lime-500 hover:to-lime-400 transition-all flex items-center space-x-2 shadow-lg shadow-lime-500/20"
                      >
                        <SafeIcon icon={FiCheck} className="w-4 h-4" />
                        <span>Confirm Delivery</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {activeTab === 'delivered' && (
            <div className="space-y-4">
              {dispatchData.delivered.map((order) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-lime-500/5 border border-lime-500/20 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-lime-500 rounded-full p-1">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-[#e8f5e9]">{order.orderId}</h4>
                        <p className="text-sm text-[#6b8772]">{order.customer}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#e8f5e9]">N$ {order.totalValue.toLocaleString()}</p>
                      <p className="text-sm text-lime-400">Delivered at {order.deliveredAt}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center space-x-2 text-sm text-[#a5c4ad]">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 text-[#6b8772]" />
                      <span>{order.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-[#a5c4ad]">
                      <SafeIcon icon={FiTruck} className="w-4 h-4 text-[#6b8772]" />
                      <span>Driver: {order.driver}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-[#6b8772]">{order.items} items delivered</span>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className={`w-3 h-3 rounded-full ${i < order.rating ? 'bg-amber-400' : 'bg-[#243129]'}`} />
                        ))}
                      </div>
                    </div>
                    <button className="text-[#84cc16] hover:text-lime-300 text-sm font-medium transition-colors">
                      View Proof of Delivery
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dispatch Note Preview Modal */}
      <AnimatePresence>
        {dispatchNotePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setDispatchNotePreview(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-[#243129]">
                <div>
                  <h2 className="text-xl font-display font-semibold text-[#e8f5e9]">Dispatch Note</h2>
                  <p className="text-sm font-mono text-[#6b8772] mt-1">{dispatchNotePreview.orderId}</p>
                </div>
                <button
                  onClick={() => setDispatchNotePreview(null)}
                  className="text-[#6b8772] hover:text-[#e8f5e9] p-2 hover:bg-[#1c2822] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Dispatch Note Content */}
              <div className="p-5 space-y-4">
                {/* Company Header */}
                <div className="text-center pb-4 border-b border-[#243129]">
                  <h3 className="text-lg font-display font-semibold text-lime-400">Fresh Produce Co.</h3>
                  <p className="text-sm font-mono text-[#6b8772]">Dispatch Document</p>
                </div>

                {/* Order Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-mono text-[#6b8772] uppercase">Order ID</p>
                    <p className="text-sm font-mono text-[#e8f5e9]">{dispatchNotePreview.orderId}</p>
                  </div>
                  <div>
                    <p className="text-xs font-mono text-[#6b8772] uppercase">Priority</p>
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(dispatchNotePreview.priority)}`}>
                      {dispatchNotePreview.priority.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4">
                  <p className="text-xs font-mono text-[#6b8772] uppercase mb-2">Customer</p>
                  <p className="text-sm font-medium text-[#e8f5e9]">{dispatchNotePreview.customer}</p>
                  <div className="flex items-start gap-2 mt-2">
                    <SafeIcon icon={FiMapPin} className="w-4 h-4 text-[#6b8772] mt-0.5" />
                    <p className="text-sm text-[#a5c4ad]">{dispatchNotePreview.address}</p>
                  </div>
                </div>

                {/* Delivery Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4">
                    <p className="text-xs font-mono text-[#6b8772] uppercase mb-1">Time Slot</p>
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-lime-400" />
                      <p className="text-sm font-medium text-[#e8f5e9]">{dispatchNotePreview.timeSlot}</p>
                    </div>
                  </div>
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4">
                    <p className="text-xs font-mono text-[#6b8772] uppercase mb-1">Items</p>
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiPackage} className="w-4 h-4 text-lime-400" />
                      <p className="text-sm font-medium text-[#e8f5e9]">{dispatchNotePreview.items} items</p>
                    </div>
                  </div>
                </div>

                {/* Total Value */}
                <div className="flex items-center justify-between pt-3 border-t border-[#243129]">
                  <span className="text-sm font-mono text-[#6b8772]">Total Value:</span>
                  <span className="text-xl font-display font-semibold text-lime-400">
                    N$ {dispatchNotePreview.totalValue.toLocaleString()}
                  </span>
                </div>

                {/* Notes */}
                {dispatchNotePreview.notes && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-xs font-mono text-amber-400 uppercase mb-1">Special Instructions</p>
                    <p className="text-sm text-amber-300">{dispatchNotePreview.notes}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-5 border-t border-[#243129] flex items-center gap-3">
                <button
                  onClick={() => setDispatchNotePreview(null)}
                  className="flex-1 bg-[#1c2822] border border-[#243129] text-[#a5c4ad] px-4 py-3 rounded-lg hover:border-[#84cc16]/30 transition-colors font-mono text-sm font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    window.print();
                  }}
                  className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-3 rounded-lg shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow font-mono text-sm font-medium flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                  Print
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dispatch;
